(function (global) {
  'use strict';

  /* ------------------------------------------------------------ constants */

  var LAYER_KEYS = {
    'Traffic|Channel Selection': 't1',
    'Traffic|Traffic Qualification': 't2',
    'Traffic|Page Handoff': 't3',
    'Conversion|Offer Design': 'c1',
    'Conversion|Page Alignment': 'c2',
    'Conversion|Friction Removal': 'c3',
    'Retention|Segmentation': 'r1',
    'Retention|Purchase Loop': 'r2',
    'Retention|Trigger Timing': 'r3',
  };
  var ASK_LAYER_KEYS = { t1: true, r1: true, r3: true };
  var STATUS_LEVEL = { pass: 0, inconclusive: 0, warn: 1, fail: 2 };
  var STATUS_LABEL = { pass: 'PASS', warn: 'WARN', fail: 'FAIL', inconclusive: 'LIMITED' };
  var ISSUE_ORDER = { fail: 0, warn: 1, inconclusive: 2, pass: 3 };
  var PILLAR_KEY = { Traffic: 't', Conversion: 'c', Retention: 'r' };
  var PILLAR_ORDER = ['Traffic', 'Conversion', 'Retention', 'Technical'];

  var LAYER_SHORT = {
    'Channel Selection': 'Channel selection',
    'Traffic Qualification': 'Qualification',
    'Page Handoff': 'Page handoff',
    'Offer Design': 'Offer design',
    'Page Alignment': 'Page alignment',
    'Friction Removal': 'Friction',
    'Segmentation': 'Segmentation',
    'Purchase Loop': 'Purchase loop',
    'Trigger Timing': 'Trigger timing',
    'Technical Feel': 'Technical feel',
  };

  /* Fallback only. The backend sends verdict.plainHeadline since honest-v3. */
  var LAYER_PLAIN = {
    'Friction Removal': "Buyers hit doubts your page never answers. That's where {domain} is leaking.",
    'Offer Design': "A stranger can't tell what this is and why them, fast enough. That's where {domain} is leaking.",
    'Page Alignment': "One page is trying to serve cold, warm and hot visitors at once. That's where {domain} is leaking.",
    'Page Handoff': "The page doesn't finish the thought the click started. That's where {domain} is leaking.",
    'Traffic Qualification': "The copy speaks to everyone, so it qualifies no one. That's where {domain} is leaking.",
    'Channel Selection': "Sales start on rented attention that never becomes owned. That's where {domain} is leaking.",
    'Segmentation': "New, loyal and lapsed customers all get the same message. That's where {domain} is leaking.",
    'Purchase Loop': "Nothing is designed to bring customers back for order two. That's where {domain} is leaking.",
    'Trigger Timing': "Customers drift off quietly because nothing brings them back on time. That's where {domain} is leaking.",
    'Technical Feel': "The page feels slower than the brand it's selling. That's where {domain} is leaking.",
  };

  /* Short, honest gists for the hero stage markers (~22 chars max). */
  var LAYER_GIST = {
    'Friction Removal': 'unanswered doubts',
    'Offer Design': 'unclear promise',
    'Page Alignment': 'competing asks',
    'Page Handoff': 'broken handoff',
    'Traffic Qualification': 'speaks to everyone',
    'Channel Selection': 'rented traffic',
    'Segmentation': 'flat messaging',
    'Purchase Loop': 'no second order',
    'Trigger Timing': 'no comeback moment',
    'Technical Feel': 'slow feel',
  };

  var TRAFFIC_VALUES = ['paid-social', 'google-search', 'email-repeat', 'mixed'];
  var DISCOUNT_VALUES = ['full-price', 'sometimes', 'heavily'];

  /* ------------------------------------------------------------- utilities */

  function track(eventName, data) {
    try {
      var payload = { event: eventName };
      if (data) {
        Object.keys(data).forEach(function (key) { payload[key] = data[key]; });
      }
      global.dataLayer = global.dataLayer || [];
      global.dataLayer.push(payload);
      if (global.document && typeof global.CustomEvent === 'function') {
        global.document.dispatchEvent(new global.CustomEvent('bd:xray', { detail: payload }));
      }
    } catch (_error) { /* analytics must never break the tool */ }
  }

  function buildRequest(values) {
    var request = { storeUrl: String(values.storeUrl || '').trim() };
    var productUrl = String(values.productUrl || '').trim();
    var customerEmail = String(values.customerEmail || '').trim();

    if (productUrl) request.productUrl = productUrl;
    if (customerEmail) request.customer = { email: customerEmail };
    if (values.asks && (values.asks.trafficSource || values.asks.discountReliance)) {
      request.asks = values.asks;
    }
    if (values.monthlyRevenue > 0) {
      request.revenue = { monthlyRevenue: values.monthlyRevenue };
    }
    return request;
  }

  function parseMoney(value) {
    var digits = String(value == null ? '' : value).replace(/[^0-9.]/g, '');
    var parsed = parseFloat(digits);
    return isFinite(parsed) && parsed > 0 ? Math.round(parsed) : 0;
  }

  function displayDomain(url) {
    var raw = String(url || '').trim();
    try {
      var parsed = new URL(/^https?:\/\//i.test(raw) ? raw : 'https://' + raw);
      return parsed.hostname.replace(/^www\./, '') + (parsed.pathname === '/' ? '' : parsed.pathname.replace(/\/$/, ''));
    } catch (_error) {
      return raw;
    }
  }

  function layerKey(pillar, layer) {
    return LAYER_KEYS[String(pillar || '') + '|' + String(layer || '')] || null;
  }

  function parseResponse(payload) {
    if (!payload || payload.ok !== true || !payload.result) {
      var message = payload && typeof payload.message === 'string'
        ? payload.message
        : 'The Scan could not read that store. Please try again.';
      var error = new Error(message);
      error.code = payload && payload.code ? payload.code : 'INVALID_RESPONSE';
      throw error;
    }
    return payload.result;
  }

  function isValidStoreUrl(value) {
    var raw = String(value || '').trim();
    if (!raw || /\s/.test(raw)) return false;
    try {
      var parsed = new URL(/^https?:\/\//i.test(raw) ? raw : 'https://' + raw);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:'
        ? parsed.hostname.indexOf('.') > 0
        : false;
    } catch (_error) {
      return false;
    }
  }

  function element(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function setConsole(consoleElement, prefix, detail) {
    if (!consoleElement) return;
    consoleElement.replaceChildren(document.createTextNode(prefix));
    if (detail) {
      var strong = element('strong', '', detail);
      consoleElement.appendChild(strong);
    }
  }

  function setSubmitLoading(button, loading) {
    if (!button) return;
    if (loading) {
      if (!button.dataset.idleLabel) {
        button.dataset.idleLabel = String(button.textContent || '').trim();
      }
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = button.dataset.loadingLabel || 'Running the Scan…';
      return;
    }

    button.disabled = false;
    button.removeAttribute('aria-busy');
    if (button.dataset.idleLabel) button.textContent = button.dataset.idleLabel;
  }

  function setStatusClasses(node, status) {
    if (!node) return;
    node.classList.remove('pass', 'warn', 'fail', 'inconclusive', 'neutral');
    node.classList.add(status && STATUS_LEVEL[status] !== undefined ? status : 'neutral');
  }

  function sourceFor(check) {
    var id = String(check.id || '');
    if (id.indexOf('ai.') === 0) return 'AI judgment';
    if (id.indexOf('crux.') === 0) return 'Real-user speed';
    if (id.indexOf('render.') === 0) return 'Mobile render';
    if (id.indexOf('asks.') === 0) return 'You told us';
    return 'Page read';
  }

  function appendParams(base, params) {
    if (!base || base === '#') return base || '#';
    try {
      var url = new URL(base, global.location ? global.location.origin : 'https://example.com');
      Object.keys(params).forEach(function (key) {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          url.searchParams.set(key, params[key]);
        }
      });
      return url.pathname + url.search + url.hash;
    } catch (_error) {
      return base;
    }
  }

  /* Every array on a scan payload arrives off the wire unvalidated, and one
     legacy generation ships pillars: [null]. Reading .status off that null took
     the whole render down instead of one row, so every payload array is walked
     through here first. */
  function rows(value) {
    return Array.isArray(value) ? value.filter(Boolean) : [];
  }

  function flattenChecks(result) {
    var seen = {};
    var checks = [];
    rows(result && result.pillars).forEach(function (pillar) {
      rows(pillar.checks).forEach(function (check) {
        var key = check.id || [check.pillar, check.layer, check.finding].join('|');
        if (seen[key]) return;
        seen[key] = true;
        checks.push(check);
      });
    });
    return checks;
  }

  function isFullResult(result) {
    return Array.isArray(result.pillars) && Array.isArray(result.fixFirst);
  }

  /* Scored means we put a verdict against it. inconclusive and not-scored are
     signals we looked at and couldn't read - the same set statusLevelForTile
     refuses to give red-green weight to. */
  function isScoredStatus(status) {
    return status === 'pass' || status === 'warn' || status === 'fail';
  }

  function hasScoredSignals(result) {
    var scored = function (item) { return isScoredStatus(item && item.status); };
    return flattenChecks(result).some(scored)
      || rows(result && result.teaserChecks).some(scored)
      || rows(result && result.pillars).some(scored)
      || rows(result && result.pillarSummary).some(scored);
  }

  /* One place that works out how much of the store we actually read, so every
     part of the card tells the reader the same story.

     v3.2 and later send readStatus. Older payloads only send limitedRead, and a
     limited read is not an unread one: the same response still returns whatever
     we did manage to score. So for those we call it blocked when nothing
     scored, not when something was hard to reach. Otherwise real findings get
     buried under "Not read" and the reader is told to try again on a scan that
     worked. */
  function readState(result) {
    var safe = result || {};
    var status = safe.readStatus || {};
    var note = status.note || (safe.limitedRead && safe.limitedRead.reason) || '';
    var unreadable = typeof status.unreadableChecks === 'number' ? status.unreadableChecks : 0;
    var quality;

    if (status.quality) {
      quality = status.quality;
    } else if (safe.limitedRead) {
      quality = hasScoredSignals(safe) ? 'partial' : 'blocked';
    } else {
      quality = 'ok';
    }

    return {
      quality: quality,
      blocked: quality === 'blocked',
      partial: quality === 'partial',
      note: note,
      unreadableChecks: unreadable,
    };
  }

  /* A scan we couldn't complete. Never dressed up as a result. */
  function isBlocked(result) {
    return readState(result).blocked;
  }

  /* Renderers should never need to decide whether scored-looking fields on a
     blocked payload are trustworthy. Strip them once at the response boundary
     while keeping readStatus intact so every surface consumes the same state. */
  function resultForReadState(result, state) {
    if (!state.blocked) return result;
    return Object.assign({}, result, {
      caveat: null,
      fixFirst: null,
      leakEstimate: null,
      moreCount: 0,
      pagesAnalysed: [],
      pillars: null,
      pillarSummary: null,
      scanSummary: null,
      teaserChecks: [],
      topIssue: null,
      verdict: null,
      verdictEvidence: null,
    });
  }

  /* ------------------------------------------------------- scan summary card */

  /* The rows on screen come from pillars on a full result and from teaserChecks
     on a gated one. The fallback summary has to count the same list, or the
     coverage line reads "0 signals read" over three visible check rows. */
  function renderedChecks(result) {
    var safe = result || {};
    return isFullResult(safe) ? flattenChecks(safe) : rows(safe.teaserChecks);
  }

  function formatScanSummary(result) {
    var checks = renderedChecks(result);
    var verdictLayer = rows(result.pillars).find(function (pillar) {
      return result.verdict
        && pillar.pillar === result.verdict.pillar
        && pillar.layer === result.verdict.layer;
    });
    var summary = result.scanSummary || {
      checksRun: checks.length,
      issuesFound: checks.filter(function (check) { return check.status === 'warn' || check.status === 'fail'; }).length,
      inconclusiveCount: checks.filter(function (check) { return check.status === 'inconclusive'; }).length,
      pagesAnalysed: rows(result.pagesAnalysed).length,
      verdictScore: verdictLayer ? verdictLayer.score : 100,
      verdictStatus: verdictLayer ? verdictLayer.status : 'pass',
      rendered: Boolean(result.rendered),
      aiEnriched: Boolean(result.aiEnriched),
    };

    var layers = rows(result.pillarSummary || result.pillars);
    var frameworkLayers = layers.filter(function (item) { return item.pillar !== 'Technical'; });
    var layersLeaking = summary.layersLeaking !== undefined
      ? summary.layersLeaking
      : frameworkLayers.filter(function (item) { return item.status === 'fail'; }).length;
    var layersTotal = summary.layersTotal !== undefined ? summary.layersTotal : 9;
    /* A gated payload carries no layer list, so this counted zero fails against a
       hardcoded nine and printed "0 of 9 layers leaking" under a headline naming
       the layer that is leaking. No layer data means no count, not a clean bill. */
    var layersKnown = summary.layersLeaking !== undefined || frameworkLayers.length > 0;

    var coverage = summary.checksRun + ' signals read · ' + summary.issuesFound + ' flagged';
    if (summary.inconclusiveCount) coverage += ' · ' + summary.inconclusiveCount + ' unreadable';

    var layerCount = layersKnown
      ? layersLeaking + ' of ' + layersTotal + ' layers leaking'
      : 'layers not scored yet';
    var fixFirstLayer = result.verdict
      ? (LAYER_SHORT[result.verdict.layer] || result.verdict.layer) + ' · ' + layerCount
      : layerCount;

    var source = [result.shopify ? 'Shopify' : 'Ecommerce'];
    source.push(summary.rendered ? 'mobile render' : 'page scan');
    if (summary.aiEnriched) source.push('AI enriched');
    source.push(result.cached ? 'recent result' : 'fresh result');

    if (isBlocked(result)) {
      return {
        page: displayDomain(result.storeUrl),
        coverage: 'scan incomplete · nothing scored',
        health: 'no verdict yet',
        /* This was still shipping "mobile render · AI enriched" beside "nothing
           scored". We can't have enriched a read we never got. */
        source: 'read blocked',
      };
    }

    return {
      page: displayDomain(result.storeUrl),
      coverage: coverage,
      health: fixFirstLayer,
      source: source.join(' · '),
    };
  }

  function renderScanSummary(engine, result) {
    var container = engine.querySelector('[data-bd-xray-scan-summary]');
    if (!container) return;
    var summary = formatScanSummary(result);
    Object.keys(summary).forEach(function (key) {
      var node = container.querySelector('[data-bd-xray-summary-' + key + ']');
      if (node) node.textContent = summary[key];
    });
    container.hidden = false;
  }

  /* ----------------------------------------------------------- verdict card */

  function plainHeadlineFor(result) {
    /* This heading sits in an aria-live region, so it is the first thing a
       screen reader says about the result. It cannot claim a leak we never
       read. */
    if (isBlocked(result)) return "We couldn't read enough of your store to score it.";
    if (result.verdict && result.verdict.plainHeadline) return result.verdict.plainHeadline;
    if (result.verdict && LAYER_PLAIN[result.verdict.layer]) {
      return LAYER_PLAIN[result.verdict.layer].replace('{domain}', displayDomain(result.storeUrl));
    }
    return result.verdict && result.verdict.headline ? result.verdict.headline : 'Your Revenue System Scan is ready.';
  }

  function setVerdictHeadline(engine, result) {
    var node = engine.querySelector('[data-bd-xray-verdict]');
    var chip = engine.querySelector('[data-bd-xray-verdict-chip]');
    if (node) node.textContent = plainHeadlineFor(result);
    if (chip) {
      if (!isBlocked(result) && result.verdict && result.verdict.pillar) {
        chip.textContent = result.verdict.pillar + ' · ' + (LAYER_SHORT[result.verdict.layer] || result.verdict.layer);
        chip.hidden = false;
      } else {
        chip.hidden = true;
      }
    }
  }

  function renderVerdictQuote(engine, result) {
    var node = engine.querySelector('[data-bd-xray-verdict-quote]');
    if (!node) return;
    /* "what we read" has to be something we read. */
    var quote = (!isBlocked(result) && result.verdict && result.verdict.evidenceQuote) || '';
    if (quote) {
      node.textContent = '// what we read · ' + quote;
      node.hidden = false;
    } else {
      node.hidden = true;
    }
  }

  function renderLimitedNote(engine, result) {
    var node = engine.querySelector('[data-bd-xray-limited]');
    if (!node) return;
    var state = readState(result);

    /* The label has to match what the rest of the card is showing. A blocked
       scan is incomplete. A partial one still scored things, so calling that
       incomplete argues with the check rows sitting right underneath it. The
       old order also meant the unreadable-signal count never printed, because a
       note comes back with almost every partial read. */
    if (state.blocked) {
      node.textContent = '// scan incomplete · ' + (state.note || "we couldn't read enough of this page to score it.");
      node.hidden = false;
      return;
    }

    if (state.note) {
      node.textContent = '// limited read · ' + state.note;
      node.hidden = false;
      return;
    }

    if (state.unreadableChecks > 0) {
      // A partial read is a footnote, not a banner.
      node.textContent = '// ' + state.unreadableChecks + ' signal'
        + (state.unreadableChecks === 1 ? '' : 's')
        + " couldn't be read on this page and were left unscored.";
      node.hidden = false;
      return;
    }

    node.hidden = true;
  }

  function renderVerdictEvidence(engine, result) {
    var container = engine.querySelector('[data-bd-xray-verdict-evidence]');
    var list = engine.querySelector('[data-bd-xray-verdict-evidence-list]');
    if (!container || !list) return;
    /* Every sibling renderer bails on a blocked read. This one did not, so a
       blocked payload that still carried a verdict printed evidence rows under
       a heading saying nothing on the page was readable. */
    if (isBlocked(result)) {
      list.replaceChildren();
      container.hidden = true;
      return;
    }
    var evidence = Array.isArray(result.verdictEvidence)
      ? rows(result.verdictEvidence)
      : flattenChecks(result).filter(function (check) {
          return result.verdict
            && check.pillar === result.verdict.pillar
            && check.layer === result.verdict.layer;
        });
    evidence = evidence.filter(function (check) {
      return !result.topIssue || check.finding !== result.topIssue.finding;
    }).slice(0, 2);

    list.replaceChildren();
    evidence.forEach(function (check) {
      var item = element('li');
      item.appendChild(element('span', 'evidence-status ' + check.status, STATUS_LABEL[check.status] || 'CHECK'));
      var body = element('span', '', check.finding);
      item.appendChild(body);
      list.appendChild(item);
    });
    container.hidden = evidence.length === 0;
  }

  function statusLevelForTile(status) {
    // inconclusive / not-scored carry no red-green weight
    if (!isScoredStatus(status)) return null;
    return status === 'fail' ? 2 : status === 'warn' ? 1 : 0;
  }

  function pillarLayerStatuses(result, pillarName) {
    var pillars = rows(result.pillars);
    if (pillars.length) {
      return pillars
        .filter(function (pillar) { return pillar.pillar === pillarName; })
        .map(function (pillar) { return pillar.status; });
    }
    return rows(result.pillarSummary)
      .filter(function (item) { return item.pillar === pillarName; })
      .map(function (item) { return item.status; });
  }

  /* Answering the two questions only moves a tile when that pillar has an
     ask-scored layer. Nothing under Conversion does, so offering "two
     questions" there sends the reader to a form that cannot change it. */
  function pillarIsAskable(pillarName) {
    var prefix = PILLAR_KEY[pillarName];
    if (!prefix) return false;
    return Object.keys(ASK_LAYER_KEYS).some(function (key) { return key.charAt(0) === prefix; });
  }

  function renderPillarTiles(engine, result, asksAnswered) {
    var blocked = isBlocked(result);
    Object.keys(PILLAR_KEY).forEach(function (pillarName) {
      var tile = engine.querySelector('.ptile[data-pillar="' + PILLAR_KEY[pillarName] + '"]');
      if (!tile) return;
      var label = tile.querySelector('.ps span');

      // Nothing was read, so nothing is "Healthy".
      if (blocked) {
        setStatusClasses(tile, null);
        if (label) label.textContent = 'Not read';
        return;
      }

      var statuses = pillarLayerStatuses(result, pillarName);
      var levels = statuses.map(statusLevelForTile).filter(function (level) { return level !== null; });

      if (!levels.length) {
        var limited = statuses.indexOf('inconclusive') >= 0;
        setStatusClasses(tile, null);
        if (label) {
          label.textContent = limited
            ? 'Limited read'
            : (pillarIsAskable(pillarName) && !asksAnswered ? 'Two questions →' : 'Not scored');
        }
        return;
      }

      var level = Math.max.apply(null, levels);
      var status = level === 2 ? 'fail' : level === 1 ? 'warn' : 'pass';
      setStatusClasses(tile, status);
      if (label) {
        label.textContent = status === 'fail' ? 'Leaking' : status === 'warn' ? 'Warning' : 'Healthy';
      }
    });
  }

  function renderTaste(engine, result, asksAnswered) {
    var taste = engine.querySelector('[data-bd-xray-taste]');
    if (!taste) return;
    taste.hidden = false;

    /* A blocked scan has no topIssue. Reading through it threw a TypeError that
       run() caught as a scan error, which left the panel half built: the tiles
       kept whatever the template shipped, so the reader was shown "Leaking" on
       a scan that read nothing. */
    var blockedScan = isBlocked(result);
    var topIssue = result.topIssue || {};

    setVerdictHeadline(engine, result);
    var dot = engine.querySelector('[data-bd-xray-verdict-dot]');
    /* Amber over "nothing scored badly enough to lead on" paints a clean store as
       a warning. Grey when we couldn't read it, green when we read it and found
       nothing to lead on, amber only when there is a real finding. */
    if (dot) {
      dot.style.background = blockedScan
        ? 'var(--muted)'
        : (result.topIssue ? 'var(--warn)' : 'var(--pass)');
    }

    var finding = engine.querySelector('[data-bd-xray-finding]');
    var findingEvidence = engine.querySelector('[data-bd-xray-finding-evidence]');
    var fix = engine.querySelector('[data-bd-xray-fix]');
    var more = engine.querySelector('[data-bd-xray-more]');
    if (finding) {
      finding.textContent = topIssue.finding
        || (blockedScan
          ? 'This storefront did not give us enough to read.'
          : 'Nothing scored badly enough to lead on.');
    }
    if (findingEvidence) {
      if (!blockedScan && topIssue.evidence) {
        findingEvidence.textContent = '// what we read · ' + topIssue.evidence;
        findingEvidence.hidden = false;
      } else {
        findingEvidence.hidden = true;
      }
    }
    if (fix) {
      fix.textContent = topIssue.fix
        || (blockedScan
          ? 'Run it again in a minute. If it keeps coming back empty, the store is blocking outside reads and we can read it by hand.'
          : 'Nothing to fix first. Work down the ranked list below.');
    }
    if (more) {
      /* Both endings live in the template and this only toggles them. Rebuilding
         the line here threw away markup it didn't own: once a blocked scan had
         swapped the ending, a successful rescan in the same session was still
         telling the reader to try again in a minute. */
      var moreEnding = engine.querySelector('[data-bd-xray-more-ending]');
      var moreBlockedEnding = engine.querySelector('[data-bd-xray-more-blocked-ending]');
      /* moreCount is optional on the payload. Without this the line read
         "undefined more issues", because undefined === 0 is false. */
      var moreCount = typeof result.moreCount === 'number' && isFinite(result.moreCount)
        ? result.moreCount
        : 0;

      more.textContent = blockedScan
        ? 'Nothing scored'
        : (moreCount === 0
          ? 'No other issues'
          : moreCount + (moreCount === 1 ? ' more issue' : ' more issues'));

      if (moreEnding) moreEnding.hidden = blockedScan;
      if (moreBlockedEnding) moreBlockedEnding.hidden = !blockedScan;
    }
    renderScanSummary(engine, result);
    renderVerdictQuote(engine, result);
    renderLimitedNote(engine, result);
    renderVerdictEvidence(engine, result);
    renderPillarTiles(engine, result, asksAnswered);
  }

  /* ---------------------------------------------------------- evidence grid */

  function checkRow(check, isTopIssue) {
    var row = element('div', 'checkrow ' + check.status);
    row.appendChild(element('span', 'cstat ' + (check.status === 'inconclusive' ? 'inconclusive' : check.status)));

    var main = element('div', 'cmain');
    main.appendChild(element('b', '', check.finding));
    var detail = check.status === 'pass'
      ? 'No action needed.'
      : check.fix;
    main.appendChild(element('span', '', detail));
    if (check.evidence && check.status !== 'pass') {
      main.appendChild(element('span', 'evq', '// what we read · ' + check.evidence));
    }
    row.appendChild(main);

    var meta = element('div', 'cmeta');
    if (isTopIssue) meta.appendChild(element('span', 'cfirst', '→ your fix-first'));
    meta.appendChild(element('span', 'cmap', LAYER_SHORT[check.layer] || check.layer));
    row.appendChild(meta);
    row.title = sourceFor(check) + (check.evidence ? ' · what we read: ' + check.evidence : '');
    return row;
  }

  function renderCheckRows(engine, result) {
    var grid = engine.querySelector('[data-bd-xray-checks]');
    var badge = engine.querySelector('[data-bd-xray-checks-badge]');
    var note = engine.querySelector('[data-bd-xray-checks-note]');
    if (!grid) return;

    grid.replaceChildren();
    var full = isFullResult(result);
    var checks = full ? flattenChecks(result) : rows(result.teaserChecks);
    var summary = result.scanSummary || {};
    var state = readState(result);

    /* The badge is aria-live, so a screen reader is read whatever it says.
       "scan complete · 24 signals read" over a card that says nothing was read
       is the version of this bug that only some people get. */
    var badgeState = state.blocked ? 'scan incomplete' : (state.partial ? 'limited read' : 'scan complete');

    if (state.note) {
      grid.appendChild(element('div', 'readnote',
        (state.blocked ? '// scan incomplete · ' : '// limited read · ') + state.note));
    } else if (state.unreadableChecks > 0) {
      grid.appendChild(element('div', 'readnote', '// limited read · ' + state.unreadableChecks
        + ' signal' + (state.unreadableChecks === 1 ? '' : 's') + " couldn't be read on this page."));
    }

    if (!checks.length) {
      var gatedRow = element('div', 'checkrow');
      gatedRow.appendChild(element('span', 'cstat neutral'));
      var gatedMain = element('div', 'cmain');
      /* This row said "Live scan complete" and promised the checks were waiting in
         the free teardown, on a scan that read nothing and has no checks to put
         there. The badge beside it already said "scan incomplete". */
      if (state.blocked) {
        gatedMain.appendChild(element('b', '', "We couldn't read this storefront"));
        gatedMain.appendChild(element('span', '', "Nothing scored, so there are no checks to show. Run it again in a minute, or start the Plan and we'll read it by hand."));
      } else {
        gatedMain.appendChild(element('b', '', 'Live scan complete'));
        gatedMain.appendChild(element('span', '', 'Individual checks are included in the free account teardown.'));
      }
      gatedRow.appendChild(gatedMain);
      grid.appendChild(gatedRow);
      /* "headline view" promised a headline summary. On a blocked read there is
         no headline, and this badge is aria-live, so it was read out over a row
         saying we could not read the store at all. */
      if (badge) {
        badge.textContent = badgeState + (state.blocked ? ' · nothing scored' : ' · headline view');
      }
    } else {
      var topIssueFinding = result.topIssue ? result.topIssue.finding : null;

      PILLAR_ORDER.forEach(function (pillarName) {
        var group = checks.filter(function (check) { return check.pillar === pillarName; });
        if (!group.length) return;
        group.sort(function (a, b) {
          var orderDelta = (ISSUE_ORDER[a.status] || 0) - (ISSUE_ORDER[b.status] || 0);
          return orderDelta !== 0 ? orderDelta : (b.impact || 0) - (a.impact || 0);
        });
        var issues = group.filter(function (check) { return check.status !== 'pass'; });
        var passes = group.filter(function (check) { return check.status === 'pass'; });

        var flaggedCount = group.filter(function (check) { return check.status === 'warn' || check.status === 'fail'; }).length;
        /* A group of rows we couldn't read was headed "clear". Unread is not
           clean, and the rows saying so were sitting directly underneath. */
        var unreadCount = group.filter(function (check) { return check.status === 'inconclusive'; }).length;
        var groupNote = flaggedCount
          ? ' · ' + flaggedCount + ' flagged'
          : (unreadCount ? ' · ' + unreadCount + " couldn't be read" : ' · clear');
        grid.appendChild(element('p', 'grouphead', '// ' + pillarName.toLowerCase() + groupNote));

        issues.forEach(function (check) {
          grid.appendChild(checkRow(check, full && topIssueFinding === check.finding));
        });

        if (passes.length) {
          if (full && passes.length > 1) {
            var toggle = element('button', 'passtoggle', '✓ ' + passes.length + ' things working · show');
            toggle.type = 'button';
            toggle.setAttribute('aria-expanded', 'false');
            var passRows = passes.map(function (check) {
              var row = checkRow(check, false);
              row.hidden = true;
              return row;
            });
            toggle.addEventListener('click', function () {
              var open = toggle.getAttribute('aria-expanded') === 'true';
              toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
              toggle.textContent = '✓ ' + passes.length + ' things working · ' + (open ? 'show' : 'hide');
              passRows.forEach(function (row) { row.hidden = !open ? false : true; });
            });
            grid.appendChild(toggle);
            passRows.forEach(function (row) { grid.appendChild(row); });
          } else {
            passes.forEach(function (check) { grid.appendChild(checkRow(check, false)); });
          }
        }
      });

      /* The bridge promised a ranked fix-list further down the page. On a
         blocked read it scrolled people to "nothing ranked", and the badge
         branch below already tells the truth for this case. */
      if (!full && !state.blocked) {
        var hiddenCount = Math.max(0, (summary.checksRun || checks.length) - checks.length);
        /* Without scanSummary.checksRun this always came out at zero, so the
           button offered "+ 0 more signals". */
        var bridge = element('button', 'bridgerow', hiddenCount > 0
          ? '+ ' + hiddenCount + ' more signals and your ranked fix-list in the free teardown ↓'
          : 'Your ranked fix-list is in the free teardown ↓');
        bridge.type = 'button';
        bridge.addEventListener('click', function () {
          var gated = engine.querySelector('[data-bd-xray-gated]');
          if (gated && gated.scrollIntoView) gated.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        grid.appendChild(bridge);
        if (badge) badge.textContent = badgeState + ' · verdict + evidence';
      } else if (badge) {
        badge.textContent = state.blocked
          ? badgeState + ' · nothing scored'
          : badgeState + ' · ' + (summary.checksRun || checks.length) + ' signals read';
      }
    }

    if (note) {
      /* The shipped caveat says we read the live storefront. On a blocked scan
         it sat directly under a row saying we could not read it. */
      note.textContent = state.blocked
        ? "We reached this storefront but couldn't read enough of it to score. No store access was used."
        : (result.caveat || 'Live output from the public storefront. No store access was used.');
    }
  }

  function swapHeadings(engine, result) {
    var heading = engine.querySelector('.head h2');
    var sub = engine.querySelector('.head .sub');
    var kicker = engine.querySelector('.autoscan .as-k');
    /* Both of these promise evidence and a ranked plan. On a blocked scan there
       is neither, and they sat straight above an empty grid saying so. */
    var blockedScan = isBlocked(result);
    if (heading) heading.textContent = 'Your Scan result for ' + displayDomain(result.storeUrl);
    if (sub) {
      sub.textContent = blockedScan
        ? "We couldn't read enough of this storefront to score it, so there is no verdict and no ranked plan below."
        : 'The verdict first, the evidence we read behind it, then the ranked plan. Top to bottom, in that order.';
    }
    if (kicker) {
      kicker.textContent = blockedScan
        ? '// the evidence · nothing on this page was readable'
        : '// the evidence · what we read on your live page';
    }
  }

  /* ---------------------------------------------------------- gated teardown */

  function renderLayerCells(engine, result, asksAnswered) {
    var pillarRows = rows(result.pillars);
    var layers = pillarRows.length ? pillarRows : rows(result.pillarSummary);
    var blockedScan = isBlocked(result);

    engine.querySelectorAll('.cell[data-layer]').forEach(function (cell) {
      setStatusClasses(cell, null);
      cell.classList.remove('first');
      var label = cell.querySelector('.st span');
      if (!label) return;
      var key = cell.getAttribute('data-layer');
      /* The two questions only move the score. Pointing at them on a read
         that scored nothing sent people to a form that cannot help, and the
         tiles above this already say "Not read". */
      label.textContent = blockedScan
        ? 'NOT READ'
        : (ASK_LAYER_KEYS[key] && !asksAnswered ? 'TWO QUESTIONS ↑' : 'NOT SCORED');
    });

    /* readState owns whether payload scores can be rendered. The boundary
       normalizer removes them too, and this return keeps the cell renderer
       honest if it is reused with an unnormalised result later. */
    if (blockedScan) return;

    layers.forEach(function (item) {
      if (item.status === 'not-scored') return;
      var key = layerKey(item.pillar, item.layer);
      if (!key) return;
      var cell = engine.querySelector('.cell[data-layer="' + key + '"]');
      if (!cell) return;
      setStatusClasses(cell, item.status === 'inconclusive' ? null : item.status);
      var label = cell.querySelector('.st span');
      if (label) label.textContent = STATUS_LABEL[item.status] || 'NOT SCORED';
    });

    /* A blocked payload can still carry a verdict. Painting one cell as the
       fix-first layer contradicted the line above saying nothing was scored. */
    if (result.verdict) {
      var firstKey = layerKey(result.verdict.pillar, result.verdict.layer);
      var firstCell = firstKey && engine.querySelector('.cell[data-layer="' + firstKey + '"]');
      if (firstCell) firstCell.classList.add('first');
    }
  }

  function toolFor(engine, fix) {
    var layer = fix.layer;
    if (layer === 'Purchase Loop' || layer === 'Segmentation' || layer === 'Trigger Timing') {
      return { name: 'LTV Calculator', url: engine.dataset.toolRetentionUrl };
    }
    if (layer === 'Friction Removal' || layer === 'Technical Feel') {
      return { name: 'Conversion Rate Calculator', url: engine.dataset.toolConversionUrl };
    }
    if (layer === 'Offer Design') {
      return { name: 'The Plan', url: engine.dataset.toolOfferUrl };
    }
    return { name: 'Temperature-Match Checker', url: engine.dataset.toolTemperatureUrl };
  }

  function handoffParams(engine, result) {
    return {
      store: displayDomain(result.storeUrl),
      /* A blocked read has no scored layer, so handing the verdict layer on
         pre-filled the quiz and the calculator with a diagnosis we never made. */
      layer: (result.verdict && !isBlocked(result)) ? (LAYER_SHORT[result.verdict.layer] || result.verdict.layer) : '',
      revenue: engine.dataset.bdXrayRevenue || '',
    };
  }

  function renderFixFirst(engine, result) {
    var list = engine.querySelector('[data-bd-xray-fix-first]');
    if (!list) return;
    list.replaceChildren(element('li', 't3h', '// fix-first, ranked by impact'));

    var fixes = rows(result.fixFirst);
    if (!fixes.length) {
      var emptyItem = element('li');
      var emptyBody = element('div', 't3b');
      /* Branching on limitedRead alone meant a v3.2 blocked scan was told
         "nothing urgent to fix" beside three tiles reading "Not read". */
      if (readState(result).quality !== 'ok') {
        emptyBody.appendChild(element('b', '', 'This one needs the rendered read'));
        emptyBody.appendChild(element('p', '', 'The public HTML hides most signals on this storefront, so a ranked fix-list from the outside would be guesswork. The Plan reads the rendered store and your actual numbers.'));
      } else {
        emptyBody.appendChild(element('b', '', 'Nothing urgent to fix'));
        emptyBody.appendChild(element('p', '', 'The current diagnostic did not find a significant issue.'));
      }
      emptyItem.appendChild(emptyBody);
      list.appendChild(emptyItem);
      return;
    }

    var params = handoffParams(engine, result);
    fixes.forEach(function (fix, index) {
      var item = element('li');
      item.appendChild(element('span', 'rk', '0' + (index + 1)));

      var body = element('div', 't3b');
      body.appendChild(element('b', '', fix.finding));
      var metaBits = [fix.pillar + ' · ' + (LAYER_SHORT[fix.layer] || fix.layer)];
      if (fix.impact) metaBits.push('impact ' + fix.impact + ' · effort ' + fix.effort);
      body.appendChild(element('span', 't3meta', metaBits.join('  ·  ')));
      body.appendChild(element('p', '', fix.fix));
      var tool = toolFor(engine, fix);
      var link = element('a', '', 'Fix it with the ' + tool.name + ' →');
      link.href = appendParams(tool.url || '#', {
        store: params.store,
        layer: LAYER_SHORT[fix.layer] || fix.layer,
        revenue: params.revenue,
      });
      link.setAttribute('data-bd-xray-tool', tool.name);
      body.appendChild(link);
      item.appendChild(body);
      item.appendChild(element('span', 'sv ' + fix.status, STATUS_LABEL[fix.status] || 'CHECK'));
      list.appendChild(item);
    });
  }

  function renderLeak(engine, result) {
    var row = engine.querySelector('[data-bd-xray-leak-row]');
    if (!row) return;
    var leak = result.leakEstimate;
    if (!leak || !isFinite(leak.monthly)) {
      row.hidden = true;
      return;
    }

    row.hidden = false;
    var value = row.querySelector('[data-bd-xray-leak]');
    if (value) {
      if (isFinite(leak.monthlyLow) && isFinite(leak.monthlyHigh)) {
        value.textContent = '~$' + Math.round(leak.monthlyLow).toLocaleString('en-NZ')
          + '–$' + Math.round(leak.monthlyHigh).toLocaleString('en-NZ') + '/mo';
      } else {
        value.textContent = '~$' + Math.round(leak.monthly).toLocaleString('en-NZ') + '/mo';
      }
    }
    var calcLink = row.querySelector('a');
    if (calcLink) {
      var params = handoffParams(engine, result);
      /* This read its own mutated href, so the first scan's layer and revenue
         stayed glued to the link through every scan after it. Every other
         handoff link already reads the base the template shipped. */
      calcLink.href = appendParams(calcLink.getAttribute('data-base-href') || calcLink.getAttribute('href') || '#', params);
    }
  }

  /* Remember what the template shipped before anything overwrites it, so a
     rescan can put it back. Writing on one branch only is how the last scan's
     copy survived onto a card that no longer meant it. */
  function shippedText(node) {
    if (!node) return '';
    if (typeof node.dataset.bdDefault !== 'string') node.dataset.bdDefault = node.textContent;
    return node.dataset.bdDefault;
  }

  function bookHref(engine, result) {
    var book = engine.querySelector('[data-bd-xray-book]');
    var base = book && (book.getAttribute('data-base-href') || book.getAttribute('href'));
    return appendParams(base || '/pages/apply', { store: displayDomain(result.storeUrl) });
  }

  /* The gate link keeps one structural selector across account and booking
     states. Keep its visible purpose and analytics identity together so a
     rescan cannot restore one without the other. */
  function renderGateLink(link, label, href, eventName) {
    if (!link) return;
    link.textContent = label;
    link.href = href;
    link.setAttribute('data-bd-xray-gate-event', eventName);
  }

  /* The gate card is one state machine, not one line of copy. Changing the body
     text on its own left the padlock, the heading and the account button
     arguing with it, and the booking action the new body promised was sitting
     inside the blurred panel where nobody could reach it. */
  function renderGateCopy(engine, result, full) {
    var lock = engine.querySelector('[data-bd-xray-gate-lock]');
    var gated = engine.querySelector('[data-bd-xray-gated]');
    var heading = engine.querySelector('[data-bd-xray-gate-heading]');
    var body = engine.querySelector('[data-bd-xray-gate-body]');
    var link = engine.querySelector('[data-bd-xray-gate-link]');
    var icon = engine.querySelector('[data-bd-xray-gate-icon]');
    var summary = result.scanSummary || {};

    var headingDefault = shippedText(heading);
    var bodyDefault = shippedText(body);
    var linkDefault = shippedText(link);
    var linkDefaultHref = link && (link.getAttribute('data-base-href') || link.getAttribute('href') || '#');

    /* A full teardown is open on the page, so there is nothing left to gate.
       Returning here left the previous scan's blocked copy sitting on the hidden
       card, ready to flash on the next gated scan before this runs again. */
    if (lock) lock.hidden = !!full;
    if (full) {
      if (heading) heading.textContent = headingDefault;
      if (body) body.textContent = bodyDefault;
      renderGateLink(link, linkDefault, linkDefaultHref, 'bd_xray_gate_click');
      if (icon) icon.hidden = false;
      return;
    }

    if (isBlocked(result)) {
      /* Nothing was scored, so there is no teardown behind this card. Asking
         for an account here sold a result we cannot build. */
      if (heading) heading.textContent = 'Nothing to unlock yet';
      if (body) body.textContent = "We couldn't read enough of this storefront to score it, so there is no teardown behind this card. Run the scan again in a minute, or start the Plan and we'll read it by hand.";
      renderGateLink(link, 'Start the Plan', bookHref(engine, result), 'bd_xray_book_click');
      if (icon) icon.hidden = true;
      return;
    }

    if (icon) icon.hidden = false;

    if (gated && gated.dataset.bdXraySignedIn === 'true') {
      /* Signed in already, so an account button is a dead end. What is missing
         here is missing from the read, not from the reader. Before this the
         whole card was skipped and they got a blurred panel with no way out. */
      if (heading) heading.textContent = 'We only got part of this one';
      if (body) body.textContent = "Some of this storefront did not answer us, so there is no ranked plan to show yet. Run the scan again in a minute, or start the Plan and we'll read it by hand.";
      renderGateLink(link, 'Start the Plan', bookHref(engine, result), 'bd_xray_book_click');
      return;
    }

    if (heading) heading.textContent = headingDefault;
    if (body) {
      body.textContent = summary.issuesFound > 0
        ? 'Your ' + summary.issuesFound + ' issues, ranked, with the evidence behind each. Free, it just needs an account.'
        : bodyDefault;
    }
    renderGateLink(link, linkDefault, linkDefaultHref, 'bd_xray_gate_click');
  }

  /* Three lines inside the teardown had no JS hooks at all, so they survived
     every payload shape: a title promising scored signals, a footer promising
     the result saves to a dashboard the reader has no account for, and a leak
     label crediting a fix-first layer that may not exist. */
  function renderTeardownFraming(engine, result, full, blocked) {
    var heading = engine.querySelector('[data-bd-xray-teardown-heading]');
    var footer = engine.querySelector('[data-bd-xray-teardown-footer]');
    var leakLabel = engine.querySelector('[data-bd-xray-leak-row] .ll');

    var headingDefault = shippedText(heading);
    var footerDefault = shippedText(footer);
    var leakDefault = shippedText(leakLabel);

    if (heading) {
      heading.textContent = blocked
        ? '// the full teardown · nothing readable to score'
        : headingDefault;
    }
    if (footer) {
      footer.textContent = full
        ? footerDefault
        : (blocked
          ? '// nothing was saved from this scan. Run it again in a minute and it will save to your dashboard.'
          : '// create your free account and this result saves to your dashboard, with the quiz pre-filled from it.');
    }
    if (leakLabel) {
      leakLabel.textContent = (full && rows(result.fixFirst).length)
        ? leakDefault
        : 'rough monthly upside from the leaks we can see';
    }
  }

  /* pointer-events:none stops the mouse, not the tab key. The two CTAs inside
     the blurred panel stayed focusable while the panel was aria-hidden, which
     is an axe aria-hidden-focus failure and drops the focus ring behind the
     gate card where nobody can see where they are. */
  function setTeardownFocusable(inner, locked) {
    var nodes = inner.querySelectorAll('a[href], button, input, select, textarea');
    Array.prototype.forEach.call(nodes, function (node) {
      if (locked) {
        if (typeof node.dataset.bdTabindex !== 'string') {
          node.dataset.bdTabindex = node.hasAttribute('tabindex') ? node.getAttribute('tabindex') : '';
        }
        node.setAttribute('tabindex', '-1');
      } else if (typeof node.dataset.bdTabindex === 'string') {
        if (node.dataset.bdTabindex === '') node.removeAttribute('tabindex');
        else node.setAttribute('tabindex', node.dataset.bdTabindex);
        delete node.dataset.bdTabindex;
      } else {
        node.removeAttribute('tabindex');
      }
    });
    if ('inert' in inner) inner.inert = !!locked;
  }

  function renderCtas(engine, result) {
    var params = handoffParams(engine, result);
    var quiz = engine.querySelector('[data-bd-xray-quiz]');
    var book = engine.querySelector('[data-bd-xray-book]');
    if (quiz) quiz.href = appendParams(quiz.getAttribute('data-base-href') || quiz.getAttribute('href') || '#', params);
    if (book) book.href = appendParams(book.getAttribute('data-base-href') || book.getAttribute('href') || '#', { store: params.store });
  }

  function renderGate(engine, result, asksAnswered) {
    var gated = engine.querySelector('[data-bd-xray-gated]');
    var inner = engine.querySelector('[data-bd-xray-gated-inner]');
    if (!gated || !inner) return;
    gated.hidden = false;

    var full = isFullResult(result);
    var blocked = isBlocked(result);
    gated.classList.toggle('open', full);
    renderLayerCells(engine, result, asksAnswered);
    renderTeardownFraming(engine, result, full, blocked);
    if (full) {
      inner.removeAttribute('aria-hidden');
      renderFixFirst(engine, result);
      renderLeak(engine, result);
    } else {
      var list = engine.querySelector('[data-bd-xray-fix-first]');
      if (list) {
        list.replaceChildren(element('li', 't3h', blocked
          ? "// nothing ranked · we couldn't read this store"
          : '// your ranked fixes · free with an account'));
      }
      var leak = engine.querySelector('[data-bd-xray-leak-row]');
      if (leak) leak.hidden = true;
      inner.setAttribute('aria-hidden', 'true');
    }
    /* Both of these run on both branches now. Running the gate copy only on the
       gated branch is what left a stale card behind after a rescan. */
    setTeardownFocusable(inner, !full);
    renderGateCopy(engine, result, full);
    renderCtas(engine, result);
  }

  /* ------------------------------------------------------------ hero markers */

  function renderStageMarkers(stage, result) {
    if (!stage) return;
    var markers = stage.querySelectorAll('.marker');
    if (!markers.length) return;
    var issues = rows(isFullResult(result) ? result.fixFirst : result.teaserChecks)
      .filter(function (check) { return check.status === 'warn' || check.status === 'fail'; })
      .slice(0, markers.length);

    markers.forEach ? void 0 : void 0;
    Array.prototype.forEach.call(markers, function (marker, index) {
      var text = marker.querySelector('text');
      var issue = issues[index];
      if (issue && text) {
        var label = (PILLAR_KEY[issue.pillar] ? issue.pillar.toLowerCase() : 'signal')
          + ' · ' + (LAYER_GIST[issue.layer] || (LAYER_SHORT[issue.layer] || '').toLowerCase());
        text.textContent = label.length > 24 ? (LAYER_GIST[issue.layer] || 'flagged') : label;
        marker.style.opacity = '';
        marker.removeAttribute('aria-hidden');
      } else {
        /* opacity:0 leaves the label in the accessibility tree, so a screen
           reader read the previous scan's leaks, or the demo labels the
           template ships with, as if they belonged to this result. */
        if (text) text.textContent = '';
        marker.style.opacity = '0';
        marker.setAttribute('aria-hidden', 'true');
      }
    });
  }

  /* ------------------------------------------------------------------- init */

  function resetBlockedQueueForm(engine, storeUrl) {
    var form = engine.querySelector('[data-bd-xray-queue-form]');
    var storeKey = String(storeUrl || '').trim().toLowerCase();
    if (!form || form.dataset.blockedStore === storeKey) return;

    form.dataset.blockedStore = storeKey;
    delete form.dataset.queueFingerprint;
    delete form.dataset.queueIdempotencyKey;

    var email = form.querySelector('[data-bd-xray-queue-email]');
    var submit = form.querySelector('[data-bd-xray-queue-submit]');
    var status = form.querySelector('[data-bd-xray-queue-status]');
    if (email) email.readOnly = false;
    setSubmitLoading(submit, false);
    if (status) {
      status.hidden = true;
      status.textContent = '';
    }
  }

  /* readState owns the meaning of the backend payload. This function is the
     only writer of which result surface is visible, so a retry cannot leave a
     verdict, account gate, or question form behind the blocked queue panel. */
  function applyReadSurface(engine, state) {
    var panel = engine.querySelector('[data-bd-xray-blocked]');
    var taste = engine.querySelector('[data-bd-xray-taste]');
    var asks = engine.querySelector('[data-bd-xray-asks]');
    var gated = engine.querySelector('[data-bd-xray-gated]');
    var checks = engine.querySelector('.autoscan');
    var blocked = state.blocked;

    if (panel) panel.hidden = !blocked;
    if (taste) taste.hidden = blocked;
    if (asks) asks.hidden = blocked;
    if (gated) gated.hidden = blocked;
    if (checks) checks.hidden = blocked;
  }

  function renderBlockedQueue(engine, result) {
    var panel = engine.querySelector('[data-bd-xray-blocked]');
    resetBlockedQueueForm(engine, result && result.storeUrl);

    var store = panel && panel.querySelector('[data-bd-xray-blocked-store]');
    if (store) store.textContent = displayDomain(result && result.storeUrl);

    var email = panel && panel.querySelector('[data-bd-xray-queue-email]');
    if (email && !email.value && engine.dataset.customerEmail) {
      email.value = engine.dataset.customerEmail;
    }
  }

  function queueIdempotencyKey(form, storeUrl, email) {
    var fingerprint = String(storeUrl || '').trim().toLowerCase()
      + '|' + String(email || '').trim().toLowerCase();
    if (form.dataset.queueFingerprint === fingerprint && form.dataset.queueIdempotencyKey) {
      return form.dataset.queueIdempotencyKey;
    }

    var randomPart;
    if (global.crypto && typeof global.crypto.randomUUID === 'function') {
      randomPart = global.crypto.randomUUID();
    } else {
      randomPart = Date.now().toString(36)
        + '-' + Math.random().toString(36).slice(2)
        + Math.random().toString(36).slice(2);
    }
    var key = ('theme-scan-' + randomPart).slice(0, 128);
    form.dataset.queueFingerprint = fingerprint;
    form.dataset.queueIdempotencyKey = key;
    return key;
  }

  async function queueBlockedScan(engine, storeUrl, request) {
    var form = engine.querySelector('[data-bd-xray-queue-form]');
    var email = form && form.querySelector('[data-bd-xray-queue-email]');
    var submit = form && form.querySelector('[data-bd-xray-queue-submit]');
    var status = form && form.querySelector('[data-bd-xray-queue-status]');
    if (!form || !email || !isValidStoreUrl(storeUrl)) return;
    if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;

    setSubmitLoading(submit, true);
    if (status) {
      status.hidden = false;
      status.textContent = '// starting your browser scan…';
    }

    try {
      var queueRequest = {
        idempotencyKey: queueIdempotencyKey(form, storeUrl, email.value),
        storeUrl: storeUrl,
        email: String(email.value || '').trim(),
        transactionalResultConsent: true,
      };
      var optional = request || {};
      if (optional.productUrl) queueRequest.productUrl = optional.productUrl;
      if (optional.revenue) queueRequest.revenue = optional.revenue;
      if (optional.asks) queueRequest.asks = optional.asks;

      var response = await global.fetch(engine.dataset.queueEndpoint || '/apps/tools/scan-jobs', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(queueRequest),
      });
      var payload;
      try {
        payload = await response.json();
      } catch (_error) {
        payload = null;
      }
      if (!response.ok || (payload && payload.ok === false)) {
        throw new Error(payload && payload.message
          ? payload.message
          : 'We could not start this scan. Please try again.');
      }

      setSubmitLoading(submit, false);
      submit.disabled = true;
      submit.textContent = 'Scan requested';
      email.readOnly = true;
      if (status) {
        status.textContent = '// requested · we will email your private result when it is ready.';
      }
      track('bd_xray_queue_submitted', { store: storeUrl });
    } catch (error) {
      setSubmitLoading(submit, false);
      if (status) {
        status.textContent = '// queue stopped · ' + (error && error.message
          ? error.message
          : 'Please try again.');
      }
      track('bd_xray_queue_error', {
        store: storeUrl,
        code: error && error.code ? error.code : 'QUEUE_FAILED',
      });
    }
  }

  function renderResult(engine, result, asksAnswered, state) {
    var read = state || readState(result);
    applyReadSurface(engine, read);
    if (read.blocked) {
      renderBlockedQueue(engine, result);
      return;
    }
    renderCheckRows(engine, result);
    renderTaste(engine, result, asksAnswered);
    renderGate(engine, result, asksAnswered);
    swapHeadings(engine, result);
  }

  function init(documentRoot) {
    var doc = documentRoot || global.document;
    if (!doc || !doc.querySelector) return;

    var hero = doc.querySelector('[data-bd-xray-hero]');
    var engine = doc.querySelector('[data-bd-xray-engine]');
    if (!hero || !engine || hero.dataset.bdXrayReady === 'true') return;
    hero.dataset.bdXrayReady = 'true';
    hero.classList.add('has-js');

    var form = hero.querySelector('[data-bd-xray-form]');
    var input = hero.querySelector('[data-bd-xray-store-url]');
    var productInput = hero.querySelector('[data-bd-xray-product-url]');
    var consoleElement = hero.querySelector('[data-bd-xray-console]');
    var stage = hero.querySelector('[data-bd-xray-stage]');
    var stageAddress = hero.querySelector('[data-bd-xray-stage-address]');
    var stageStatus = hero.querySelector('[data-bd-xray-stage-status]');
    var submit = form && form.querySelector('[type="submit"]');
    var asksForm = engine.querySelector('[data-bd-xray-asks]');
    var queueForm = engine.querySelector('[data-bd-xray-queue-form]');
    var reduceMotion = global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var timers = [];
    var runId = 0;
    var lastStoreUrl = '';
    var currentAsks = null;
    var currentRevenue = 0;
    var asksApplied = false;
    var verdictSeen = false;
    var verdictObserver = null;

    function clearProgress() {
      timers.forEach(global.clearTimeout);
      timers = [];
    }

    function setResultsState(state) {
      var results = engine.querySelector('[data-bd-xray-results]');
      engine.dataset.scanState = state;
      if (results) results.setAttribute('aria-busy', state === 'scanning' ? 'true' : 'false');
    }

    function loading(storeUrl, quiet) {
      clearProgress();
      if (!quiet) {
        var taste = engine.querySelector('[data-bd-xray-taste]');
        var gated = engine.querySelector('[data-bd-xray-gated]');
        var blocked = engine.querySelector('[data-bd-xray-blocked]');
        var checks = engine.querySelector('.autoscan');
        if (taste) taste.hidden = true;
        if (gated) gated.hidden = true;
        if (blocked) blocked.hidden = true;
        if (checks) checks.hidden = false;
      }
      var checksBadge = engine.querySelector('[data-bd-xray-checks-badge]');
      var checksNote = engine.querySelector('[data-bd-xray-checks-note]');
      if (checksBadge) checksBadge.textContent = quiet ? 'updating · your answers' : 'live scan · running';
      if (!quiet && checksNote) checksNote.textContent = 'Your new live checks will replace these preview rows as soon as the scan finishes.';
      if (stageAddress) stageAddress.textContent = storeUrl;
      if (stageStatus) stageStatus.textContent = 'scanning';
      if (stage && !quiet) {
        stage.classList.remove('idle', 'done');
        stage.classList.add('scanning');
      }
      setSubmitLoading(submit, true);
      if (!quiet) setConsole(consoleElement, '// fetching storefront…');
      setResultsState('scanning');

      if (!quiet) {
        [
          [2200, '// reading mobile, copy and structure…'],
          [6500, '// checking traffic, conversion and retention…'],
          [11000, '// scoring the live signals…'],
        ].forEach(function (step) {
          timers.push(global.setTimeout(function () {
            setConsole(consoleElement, step[1]);
          }, step[0]));
        });
      }
    }

    function finish(status, resultsState) {
      clearProgress();
      if (stageStatus) stageStatus.textContent = status;
      if (stage) {
        stage.classList.remove('scanning', 'idle', 'done');
        stage.classList.add(status === 'done' ? 'done' : 'idle');
      }
      setSubmitLoading(submit, false);
      /* A scan we could not read is neither a success nor a failed request.
         Left on 'complete' the badge draws a green tick over "nothing scored". */
      setResultsState(resultsState || (status === 'done' ? 'complete' : 'error'));
    }

    function scrollToVerdict() {
      var taste = engine.querySelector('[data-bd-xray-taste]');
      var target = taste && !taste.hidden ? taste : engine;
      if (target.scrollIntoView) {
        target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      }
    }

    /* The panel this watches renders on a blocked read too, saying "Not read".
       Two things follow. Arming over that panel counts a verdict view for a
       verdict that does not exist, and it burns the session flag, so the real
       view on the retry after it never fires. Disarming matters just as much:
       an observer armed by an earlier good scan is still attached to the same
       panel, so a blocked rescan would trip it whether this scan armed one or
       not. Every call now clears the last one first. */
    function watchVerdictView(blocked) {
      if (verdictObserver) {
        verdictObserver.disconnect();
        verdictObserver = null;
      }
      if (blocked || verdictSeen || typeof global.IntersectionObserver !== 'function') return;
      var taste = engine.querySelector('[data-bd-xray-taste]');
      if (!taste) return;
      var observer = new global.IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !verdictSeen) {
            verdictSeen = true;
            track('bd_xray_verdict_viewed', { store: lastStoreUrl });
            observer.disconnect();
            if (verdictObserver === observer) verdictObserver = null;
          }
        });
      }, { threshold: 0.4 });
      verdictObserver = observer;
      observer.observe(taste);
    }

    function collectAsks() {
      if (!asksForm) return null;
      var traffic = asksForm.querySelector('[data-bd-xray-q1] input:checked');
      var discount = asksForm.querySelector('[data-bd-xray-q2] input:checked');
      var revenueInput = asksForm.querySelector('[data-bd-xray-revenue]');
      currentRevenue = revenueInput ? parseMoney(revenueInput.value) : 0;
      engine.dataset.bdXrayRevenue = currentRevenue ? String(currentRevenue) : '';
      return {
        trafficSource: traffic ? TRAFFIC_VALUES[parseInt(traffic.value, 10)] : undefined,
        discountReliance: discount ? DISCOUNT_VALUES[parseInt(discount.value, 10)] : undefined,
      };
    }

    async function run(storeUrl, options) {
      var opts = options || {};
      var currentRun = ++runId;
      var url = String(storeUrl || '').trim();
      if (!isValidStoreUrl(url)) {
        setConsole(consoleElement, '// check the URL. ', 'Use a full store domain such as yourstore.com.');
        if (input) input.focus();
        return;
      }
      lastStoreUrl = url;

      if (global.history && global.history.replaceState) {
        var nextUrl = new URL(global.location.href);
        nextUrl.searchParams.set('store', url);
        global.history.replaceState({}, '', nextUrl.toString());
      }

      loading(url, Boolean(opts.quiet));
      if (!opts.quiet) {
        scrollToVerdict();
        track('bd_xray_scan_started', { store: url });
      }
      var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      var timeout = controller && global.setTimeout(function () { controller.abort(); }, 35000);

      try {
        /* Liquid owns the primary endpoint. This active-route fallback only
           covers cached or partially deployed markup with an empty attribute. */
        var response = await global.fetch(engine.dataset.endpoint || '/apps/tools/xray', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(buildRequest({
            storeUrl: url,
            productUrl: productInput && productInput.value,
            customerEmail: engine.dataset.customerEmail,
            asks: currentAsks,
            monthlyRevenue: currentRevenue,
          })),
          signal: controller ? controller.signal : undefined,
        });
        var payload;
        try {
          payload = await response.json();
        } catch (_error) {
          throw new Error('The Revenue System Scan connection is not ready yet. Please try again shortly.');
        }
        var rawResult = parseResponse(payload);
        if (currentRun !== runId) return;
        var read = readState(rawResult);
        var result = resultForReadState(rawResult, read);
        renderResult(engine, result, asksApplied, read);
        renderStageMarkers(stage, result);
        finish(read.blocked ? 'incomplete' : 'done', read.blocked ? 'incomplete' : 'complete');
        watchVerdictView(read.blocked);
        if (!opts.quiet) {
          var summary = result.scanSummary || {};
          track('bd_xray_scan_completed', {
            store: url,
            verdict_layer: (result.verdict && !read.blocked) ? result.verdict.layer : '',
            issues: typeof summary.issuesFound === 'number' ? summary.issuesFound : 0,
            /* read_quality is the honest field. limited_read keeps its name for
               the dashboards already built on it, but it now covers both
               payload shapes instead of only the pre-v3.2 one, which was
               logging blocked v3.2 scans as clean completions. */
            read_quality: read.quality,
            blocked_read: read.blocked,
            limited_read: read.quality !== 'ok',
            gated: !isFullResult(result),
          });
        }
        /* The read decides the message before quiet does. Answering the two
           questions on a store we cannot read scores nothing, so the rescan
           that says the answers are in the result is the one line this branch
           can never honestly send. */
        if (read.blocked) {
          setConsole(consoleElement, '// scan incomplete. ', "We couldn't read enough of your storefront to score it. Try again in a minute.");
        } else if (opts.quiet) {
          setConsole(consoleElement, '// updated. ', 'Your answers are scored into the result.');
        } else {
          setConsole(consoleElement, '// done. ', result.cached ? 'Live result loaded from the recent scan.' : 'Your live storefront has been analysed.');
        }
        if (!opts.quiet) scrollToVerdict();
      } catch (error) {
        if (currentRun !== runId) return;
        var message = error && error.name === 'AbortError'
          ? 'The scan took too long. Please try again.'
          : error.message;
        var checksBadge = engine.querySelector('[data-bd-xray-checks-badge]');
        var checksNote = engine.querySelector('[data-bd-xray-checks-note]');
        if (checksBadge) checksBadge.textContent = 'scan stopped · no new result';
        if (checksNote) checksNote.textContent = 'No new live result was loaded. Check the store URL and run the Scan again.';
        finish('error');
        track('bd_xray_scan_error', { store: url, code: error && error.code ? error.code : 'UNKNOWN' });
        setConsole(consoleElement, '// scan stopped. ', message);
      } finally {
        if (timeout) global.clearTimeout(timeout);
      }
    }

    function applyAsksNow() {
      if (!lastStoreUrl) return;
      currentAsks = collectAsks();
      asksApplied = true;
      track('bd_xray_asks_answered', {
        store: lastStoreUrl,
        traffic: currentAsks && currentAsks.trafficSource,
        discount: currentAsks && currentAsks.discountReliance,
        has_revenue: currentRevenue > 0,
      });
      run(lastStoreUrl, { quiet: true });
    }

    if (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        run(input && input.value);
      });
    }

    if (queueForm) {
      queueForm.addEventListener('submit', function (event) {
        event.preventDefault();
        queueBlockedScan(engine, lastStoreUrl, {
          productUrl: productInput && String(productInput.value || '').trim(),
          revenue: currentRevenue > 0 ? { monthlyRevenue: currentRevenue } : undefined,
          asks: currentAsks,
        });
      });
    }

    if (asksForm) {
      asksForm.addEventListener('submit', function (event) {
        event.preventDefault();
        applyAsksNow();
      });
      var debounce = null;
      asksForm.addEventListener('change', function () {
        if (!asksApplied) return; // first apply is explicit; afterwards it's live
        global.clearTimeout(debounce);
        debounce = global.setTimeout(applyAsksNow, 400);
      });
    }

    engine.addEventListener('click', function (event) {
      var target = event.target && event.target.closest ? event.target.closest('a, button') : null;
      if (!target) return;
      if (target.hasAttribute('data-bd-xray-gate-link')) {
        track(target.getAttribute('data-bd-xray-gate-event') || 'bd_xray_gate_click', { store: lastStoreUrl });
      } else if (target.hasAttribute('data-bd-xray-quiz')) {
        track('bd_xray_quiz_click', { store: lastStoreUrl });
      } else if (target.hasAttribute('data-bd-xray-book')) {
        track('bd_xray_book_click', { store: lastStoreUrl });
      } else if (target.hasAttribute('data-bd-xray-tool')) {
        track('bd_xray_tool_click', { store: lastStoreUrl, tool: target.getAttribute('data-bd-xray-tool') });
      }
    });

    if (input && global.location && global.location.search) {
      var prefilledStore = new URLSearchParams(global.location.search).get('store');
      if (prefilledStore && isValidStoreUrl(prefilledStore)) {
        input.value = prefilledStore;
        global.setTimeout(function () { run(prefilledStore); }, 120);
      } else if (stage) {
        stage.classList.add('idle');
      }
    }
  }

  global.BetterDigitalStoreXray = {
    applyReadSurface: applyReadSurface,
    buildRequest: buildRequest,
    formatScanSummary: formatScanSummary,
    init: init,
    isValidStoreUrl: isValidStoreUrl,
    layerKey: layerKey,
    parseMoney: parseMoney,
    parseResponse: parseResponse,
    plainHeadlineFor: plainHeadlineFor,
    queueBlockedScan: queueBlockedScan,
    queueIdempotencyKey: queueIdempotencyKey,
    resetBlockedQueueForm: resetBlockedQueueForm,
    setSubmitLoading: setSubmitLoading,
    track: track,
  };

  if (global.document) {
    if (global.document.readyState === 'loading') {
      global.document.addEventListener('DOMContentLoaded', function () { init(global.document); });
    } else {
      init(global.document);
    }
    global.document.addEventListener('shopify:section:load', function () { init(global.document); });
  }
})(typeof window !== 'undefined' ? window : globalThis);
